import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import OrgSettingsReceiptRulesBoxContent from 'src/components/OrgSettingsReceiptRulesBox/OrgSettingsReceiptRulesBoxContent'
import OrgSettingsReceiptRulesForm from 'src/components/OrgSettingsReceiptRulesForm/OrgSettingsReceiptRulesForm'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { orgCurrencyQueries } from 'src/queries/organizations/currencies/currencies.queries'
import { orgReceiptRulesQueries } from 'src/queries/organizations/receiptRules/receiptRules.queries'
import { css } from 'styled-components'

interface OrgSettingsReceiptRulesBoxProps {
  user?: UserListModel
  organization: OrganizationsListModel
}

const OrgSettingsReceiptRulesBox: React.FC<OrgSettingsReceiptRulesBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const { user, organization } = props
  const { id } = organization

  const receiptRulesQuery = useQuery(orgReceiptRulesQueries.detail(id))
  const receiptRule = receiptRulesQuery.data?.receiptRule
  const currenciesQuery = useQuery(orgCurrencyQueries.list(id))
  const loadingReceiptRules =
    receiptRulesQuery.isPending || currenciesQuery.isPending
  const [isReceiptRulesBeingEdited, setIsReceiptRulesBeingEdited] =
    useState<boolean>(false)

  return (
    <Box className={'flex-column'} noShadow outlineBorder>
      {loadingReceiptRules ? (
        <LoadingBackgroundPage />
      ) : (
        <>
          <div
            css={css`
              margin-bottom: 16px;
              display: flex;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  20,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('Receipt Rules')}
            </span>
            {user?.hasOrgAdminPermissions && !isReceiptRulesBeingEdited && (
              <HoverableLink
                onClick={(): void => {
                  setIsReceiptRulesBeingEdited(true)
                }}
                ariaLabelSource={'Update'}
                css={css`
                  display: flex;
                  align-items: center;
                `}
                testId={'updateReceiptRulesBtn'}
              >
                {translate('Update')}
                <SvgUpdate
                  css={css`
                    transform: scale(0.7);
                    margin-left: 4px;
                  `}
                />
              </HoverableLink>
            )}
          </div>
          {isReceiptRulesBeingEdited && currenciesQuery.data ? (
            <OrgSettingsReceiptRulesForm
              receiptRules={receiptRule}
              currencies={currenciesQuery.data}
              closeReceiptRulesForm={(): void => {
                setIsReceiptRulesBeingEdited(false)
              }}
              orgId={id}
              hasRecurringCards={!!receiptRulesQuery.data?.hasRecurringCards}
            />
          ) : (
            <OrgSettingsReceiptRulesBoxContent receiptRule={receiptRule} />
          )}
        </>
      )}
    </Box>
  )
}

export default OrgSettingsReceiptRulesBox
